import {
  CLASS_B_PRACTICAL_EXAM,
  PRACTICAL_EXAM_FAIL_RULE,
  scorePracticalExam,
  type PracticalExamBlock,
  type PracticalExamSheetInput,
  type PracticalExamSeverity,
} from '@lms/shared-types';

const SEVERITY_COLOR: Record<PracticalExamSeverity, string> = {
  L: '#3c64a5',
  G: '#9a3412',
  R: '#bd3f33',
};

function countOf(faults: Record<string, number>, id: string): string {
  const count = faults[id];
  return count ? String(count) : '';
}

function examDateLabel(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="pef-field">
      <span className="pef-kicker">{label}</span>
      <span className="pef-value">{value}</span>
    </div>
  );
}

function Mark({ checked }: { checked: boolean }) {
  return <span className={checked ? 'pef-mark pef-mark-on' : 'pef-mark'}>{checked ? '✓' : ''}</span>;
}

function CountBoxes({
  severity,
  value,
  readOnly,
  label,
  onChange,
}: {
  severity: PracticalExamSeverity;
  value: string;
  readOnly: boolean;
  label: string;
  onChange: (raw: string) => void;
}) {
  return (
    <span className="pef-boxes">
      {(['L', 'G', 'R'] as const).map((column) =>
        column === severity ? (
          readOnly ? (
            <span key={column} className="pef-box">
              {value}
            </span>
          ) : (
            <input
              key={column}
              inputMode="numeric"
              aria-label={label}
              value={value}
              onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 2))}
              className="pef-box pef-box-input"
            />
          )
        ) : (
          <span key={column} className="pef-box pef-box-off" />
        ),
      )}
    </span>
  );
}

function FaultColumn({
  blocks,
  faults,
  readOnly,
  onCount,
}: {
  blocks: PracticalExamBlock[];
  faults: Record<string, number>;
  readOnly: boolean;
  onCount: (id: string, raw: string) => void;
}) {
  const score = scorePracticalExam(faults);
  return (
    <div className="pef-col">
      {blocks.map((block) => {
        const sub = score.subtotals[block.id] ?? { mild: 0, serious: 0, failing: 0 };
        return (
          <section key={block.id} className="pef-block">
            <header className="pef-block-head">
              <h2>{block.title}</h2>
              <span className="pef-lgr" aria-hidden="true">
                <span>L</span>
                <span>G</span>
                <span>R</span>
              </span>
            </header>
            <ul>
              {block.faults.map((fault, index) => (
                <li key={fault.id} className={index % 2 === 0 ? 'pef-row pef-row-alt' : 'pef-row'}>
                  <span className="pef-code" style={{ color: SEVERITY_COLOR[fault.severity] }}>
                    {fault.code}
                  </span>
                  <span className="pef-fault">{fault.label}</span>
                  <CountBoxes
                    severity={fault.severity}
                    value={countOf(faults, fault.id)}
                    readOnly={readOnly}
                    label={`${fault.code} ${fault.label}`}
                    onChange={(raw) => onCount(fault.id, raw)}
                  />
                </li>
              ))}
            </ul>
            <footer className="pef-sub">
              <span>Subtotal</span>
              <span className="pef-boxes">
                <span>{sub.mild || ''}</span>
                <span>{sub.serious || ''}</span>
                <span>{sub.failing || ''}</span>
              </span>
            </footer>
          </section>
        );
      })}
    </div>
  );
}

export function PracticalExamForm({
  sheet,
  readOnly = false,
  showIdentity = true,
  showSignatures = false,
  onCount,
  onObservations,
}: {
  sheet: PracticalExamSheetInput;
  readOnly?: boolean;
  showIdentity?: boolean;
  showSignatures?: boolean;
  onCount: (id: string, raw: string) => void;
  onObservations: (value: string) => void;
}) {
  const score = scorePracticalExam(sheet.faults);
  const left = CLASS_B_PRACTICAL_EXAM.filter((block) => block.column === 'left');
  const right = CLASS_B_PRACTICAL_EXAM.filter((block) => block.column === 'right');
  return (
    <article className="pef">
      <style>{`
        .pef {
          color: #1a1d21;
          background: #fff;
          border: 1px solid #d5d8de;
          padding: 16px 18px 14px;
          font-family: Arial, Helvetica, sans-serif;
        }
        .pef-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 12px; }
        .pef-title { margin: 0; font-size: 18px; line-height: 1.15; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
        .pef-subtitle { margin: 2px 0 0; color: #6b7280; font-size: 12px; }
        .pef-brand { margin: 4px 0 0; max-width: 180px; text-align: right; font-size: 16px; font-weight: 700; line-height: 1.1; }
        .pef-grid { display: grid; gap: 4px; }
        .pef-line { display: grid; gap: 4px; }
        .pef-line-1 { grid-template-columns: minmax(0, 1.7fr) minmax(88px, 0.62fr) minmax(96px, 0.55fr); }
        .pef-line-2 { grid-template-columns: minmax(0, 1.35fr) minmax(0, 0.75fr) repeat(4, minmax(64px, 0.55fr)); }
        .pef-line-3 { grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.75fr) minmax(72px, 0.5fr) minmax(140px, 0.9fr); }
        .pef-field { min-width: 0; border: 1px solid #9aa1a9; padding: 2px 4px 3px; }
        .pef-kicker { display: block; color: #6b7280; font-size: 8px; line-height: 1.2; letter-spacing: 0.04em; text-transform: uppercase; }
        .pef-value {
          display: block;
          min-height: 14px;
          margin-top: 1px;
          padding: 0 2px;
          background: #fff;
          font-size: 11px;
          line-height: 14px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .pef-vehicle { display: flex; align-items: center; gap: 10px; min-height: 14px; margin-top: 1px; font-size: 11px; }
        .pef-check { display: inline-flex; align-items: center; gap: 4px; }
        .pef-mark {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 11px;
          height: 11px;
          border: 1px solid #9aa1a9;
          background: #fff;
          font-size: 9px;
          line-height: 1;
        }
        .pef-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 14px; margin-top: 12px; align-items: start; }
        .pef-col { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
        .pef-block { break-inside: avoid; }
        .pef-block-head, .pef-row, .pef-sub { display: grid; grid-template-columns: 28px minmax(0, 1fr) 72px; align-items: center; column-gap: 6px; }
        .pef-block-head h2 { margin: 0; grid-column: 1 / 3; font-size: 10px; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase; }
        .pef-lgr, .pef-boxes { display: grid; grid-template-columns: repeat(3, 20px); justify-content: end; gap: 4px; }
        .pef-lgr span { color: #6b7280; font-size: 8px; font-weight: 700; text-align: center; }
        .pef-row { min-height: 18px; padding: 1px 0; }
        .pef-row-alt { background: #f4f6f8; }
        .pef-code { font-size: 10px; font-weight: 700; }
        .pef-fault { min-width: 0; font-size: 11px; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .pef-box {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 16px;
          border: 1px solid #9aa1a9;
          background: #fff;
          color: #1a1d21;
          font-size: 10px;
          line-height: 1;
          text-align: center;
        }
        .pef-box-off { border-color: transparent; background: #d8dce1; }
        .pef-box-input { padding: 0; font-family: inherit; outline: none; }
        .pef-box-input:focus { border-color: #1a1d21; }
        .pef-sub { margin-top: 2px; color: #6b7280; font-size: 8px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
        .pef-sub > span:first-child { grid-column: 1 / 3; text-align: right; padding-right: 4px; }
        .pef-sub .pef-boxes span { color: #1a1d21; font-size: 10px; font-weight: 700; text-align: center; text-transform: none; }
        .pef-total { display: grid; grid-template-columns: auto 1fr auto; gap: 12px; align-items: center; margin-top: 12px; padding-top: 8px; border-top: 1.5px solid #1a1d21; }
        .pef-total h2 { margin: 0 0 6px; font-size: 12px; font-weight: 700; letter-spacing: 0.02em; }
        .pef-sums { display: flex; gap: 10px; }
        .pef-sum { width: 64px; }
        .pef-sum span { display: block; font-size: 8px; font-weight: 700; letter-spacing: 0.04em; }
        .pef-sum strong { display: block; min-height: 16px; margin-top: 2px; border: 1px solid #1a1d21; background: #fff; text-align: center; font-size: 12px; line-height: 16px; }
        .pef-result { border: 1.5px solid #1a1d21; padding: 4px 8px; }
        .pef-result p { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 2px 0; font-size: 11px; font-weight: 700; }
        .pef-rule { margin: 6px 0 0; font-size: 11px; }
        .pef-rule strong { color: #6b7280; font-size: 9px; letter-spacing: 0.04em; }
        .pef-notes { display: block; width: 46%; min-width: 220px; margin-top: 8px; }
        .pef-notes textarea {
          display: block;
          width: 100%;
          height: 40px;
          margin-top: 2px;
          border: 1px solid #9aa1a9;
          padding: 4px 6px;
          background: #fff;
          color: #1a1d21;
          font-family: inherit;
          font-size: 11px;
          line-height: 1.3;
          resize: none;
          outline: none;
          box-sizing: border-box;
        }
        .pef-signs { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; max-width: 460px; margin-top: 18px; margin-left: auto; }
        .pef-sign { border-bottom: 1px solid #1a1d21; min-height: 42px; display: flex; align-items: flex-end; }
        .pef-sign img { display: block; height: 40px; width: 100%; object-fit: contain; object-position: left bottom; }
        .pef-sign + p { margin: 3px 0 0; color: #6b7280; font-size: 8px; letter-spacing: 0.04em; }
        @media (max-width: 860px) {
          .pef-line-1, .pef-line-2, .pef-line-3, .pef-cols { grid-template-columns: 1fr; }
          .pef-total { grid-template-columns: 1fr; }
          .pef-notes { width: 100%; }
        }
        @media print {
          .pef { border: 0; padding: 0; }
          .pef-cols, .pef-line-1, .pef-line-2, .pef-line-3 { grid-template-columns: revert; }
          .pef-line-1 { grid-template-columns: minmax(0, 1.7fr) minmax(88px, 0.62fr) minmax(96px, 0.55fr); }
          .pef-line-2 { grid-template-columns: minmax(0, 1.35fr) minmax(0, 0.75fr) repeat(4, minmax(64px, 0.55fr)); }
          .pef-line-3 { grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.75fr) minmax(72px, 0.5fr) minmax(140px, 0.9fr); }
          .pef-cols { grid-template-columns: 1fr 1fr; }
          .pef-total { grid-template-columns: auto 1fr auto; }
        }
      `}</style>

      {showIdentity ? (
        <>
          <header className="pef-head">
            <div>
              <h1 className="pef-title">Ficha de evaluación</h1>
              <p className="pef-subtitle">Examen práctico de conducción · clase B</p>
            </div>
            {sheet.school ? <p className="pef-brand">{sheet.school}</p> : null}
          </header>

          <div className="pef-grid">
            <div className="pef-line pef-line-1">
              <Field label="Nombres y apellidos del postulante" value={sheet.applicantName} />
              <Field label="R.U.N." value={sheet.run} />
              <Field label="Fecha del examen" value={examDateLabel(sheet.examDate)} />
            </div>
            <div className="pef-line pef-line-2">
              <Field label="Nombre del examinador" value={sheet.examinerName} />
              <Field label="Municipio" value={sheet.municipality} />
              <Field label="Hora de inicio" value={sheet.startedAt} />
              <Field label="Hora de término" value={sheet.endedAt} />
              <Field label="Km inicial" value={sheet.kmStart == null ? '' : String(sheet.kmStart)} />
              <Field label="Km final" value={sheet.kmEnd == null ? '' : String(sheet.kmEnd)} />
            </div>
            <div className="pef-line pef-line-3">
              <Field label="Escuela de conductores" value={sheet.school} />
              <Field label="Comuna de la escuela" value={sheet.schoolCommune} />
              <Field label="Patente" value={sheet.plate.toUpperCase()} />
              <div className="pef-field">
                <span className="pef-kicker">Vehículo</span>
                <span className="pef-vehicle">
                  <span className="pef-check"><Mark checked={sheet.vehicleType === 'mechanical'} /> Mecánico</span>
                  <span className="pef-check"><Mark checked={sheet.vehicleType === 'automatic'} /> Automático</span>
                </span>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <div className="pef-cols">
        <FaultColumn blocks={left} faults={sheet.faults} readOnly={readOnly} onCount={onCount} />
        <FaultColumn blocks={right} faults={sheet.faults} readOnly={readOnly} onCount={onCount} />
      </div>

      <section className="pef-total">
        <div>
          <h2>Total de faltas</h2>
          <div className="pef-sums">
            <p className="pef-sum"><span style={{ color: SEVERITY_COLOR.L }}>Leves</span><strong>{score.mild}</strong></p>
            <p className="pef-sum"><span style={{ color: SEVERITY_COLOR.G }}>Graves</span><strong>{score.serious}</strong></p>
            <p className="pef-sum"><span style={{ color: SEVERITY_COLOR.R }}>Reprobatorias</span><strong>{score.failing}</strong></p>
          </div>
        </div>
        <p className="pef-rule">
          <strong>Reprueba con: </strong>
          {PRACTICAL_EXAM_FAIL_RULE}
        </p>
        <div className="pef-result">
          <p>Aprobado <Mark checked={score.passed} /></p>
          <p>Reprobado <Mark checked={!score.passed} /></p>
        </div>
      </section>

      <label className="pef-notes">
        <span className="pef-kicker">Observaciones</span>
        <textarea
          value={sheet.observations}
          readOnly={readOnly}
          rows={1}
          onChange={(event) => onObservations(event.target.value)}
        />
      </label>

      {showSignatures ? (
        <div className="pef-signs">
          <div>
            <div className="pef-sign">
              {sheet.studentSignature ? <img src={sheet.studentSignature} alt="Firma del postulante" /> : null}
            </div>
            <p>Firma del postulante</p>
          </div>
          <div>
            <div className="pef-sign">
              {sheet.examinerSignature ? <img src={sheet.examinerSignature} alt="Firma del examinador" /> : null}
            </div>
            <p>Firma del examinador</p>
          </div>
        </div>
      ) : null}
    </article>
  );
}

function badgeClass(severity: PracticalExamSeverity, active: boolean): string {
  const base = 'flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold transition-all';
  if (!active) return `${base} bg-[#e8eaf0] text-dash-on-surface-variant`;
  if (severity === 'L') return `${base} bg-blue-600 text-white shadow-sm`;
  if (severity === 'G') return `${base} bg-amber-600 text-white shadow-sm`;
  return `${base} bg-red-600 text-white shadow-sm`;
}

export function PracticalExamPractice({
  sheet,
  readOnly = false,
  onCount,
  onObservations,
}: {
  sheet: PracticalExamSheetInput;
  readOnly?: boolean;
  onCount: (id: string, raw: string) => void;
  onObservations: (value: string) => void;
}) {
  const score = scorePracticalExam(sheet.faults);
  const approved =
    'flex items-center justify-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800';
  const failed =
    'flex items-center justify-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-800';
  const idle =
    'flex items-center justify-center gap-2 rounded-lg border border-dash-outline-variant bg-[#f4f6fb] px-3 py-2 text-sm font-medium text-dash-on-surface-variant';

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {CLASS_B_PRACTICAL_EXAM.map((block, index) => {
          const sub = score.subtotals[block.id] ?? { mild: 0, serious: 0, failing: 0 };
          return (
            <section key={block.id} className="flex flex-col overflow-hidden rounded-lg border border-dash-outline-variant bg-white">
              <header className="flex items-center justify-between gap-2 border-b border-dash-outline-variant bg-[#f4f6fb] px-3.5 py-2.5">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-[#000256] text-xs font-bold text-white">
                    {index + 1}
                  </span>
                  <h2 className="truncate text-sm font-semibold text-dash-on-surface">{block.title}</h2>
                </div>
                <span className="shrink-0 text-xs text-dash-on-surface-variant">
                  L:<b className="font-semibold">{sub.mild}</b> G:<b className="font-semibold">{sub.serious}</b> R:
                  <b className="font-semibold">{sub.failing}</b>
                </span>
              </header>
              <div className="flex flex-col divide-y divide-dash-outline-variant/50 p-2">
                {block.faults.map((fault) => {
                  const count = sheet.faults[fault.id] ?? 0;
                  const active = count > 0;
                  return (
                    <div
                      key={fault.id}
                      className={`flex items-center justify-between gap-2 rounded px-2 py-1.5 transition-colors ${
                        active ? 'bg-blue-50/40' : 'hover:bg-[#f4f6fb]/50'
                      }`}
                    >
                      <span className="pr-2 text-xs leading-tight text-dash-on-surface">
                        {fault.code} {fault.label}
                      </span>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <span className={badgeClass(fault.severity, active)}>{fault.severity}</span>
                        <div className="flex items-center rounded border border-dash-outline-variant bg-[#f4f6fb] p-0.5">
                          <button
                            type="button"
                            aria-label={`Restar falta ${fault.code} ${fault.label}`}
                            disabled={readOnly || count <= 0}
                            onClick={() => onCount(fault.id, count <= 1 ? '' : String(count - 1))}
                            className={`flex h-5 w-5 items-center justify-center rounded text-xs ${
                              !readOnly && count > 0
                                ? 'cursor-pointer font-semibold text-dash-on-surface hover:bg-white'
                                : 'cursor-not-allowed text-dash-on-surface-variant opacity-40'
                            }`}
                          >
                            -
                          </button>
                          <span className="w-5 text-center text-xs font-bold text-dash-on-surface">{count}</span>
                          <button
                            type="button"
                            aria-label={`Sumar falta ${fault.code} ${fault.label}`}
                            disabled={readOnly || count >= 99}
                            onClick={() => onCount(fault.id, String(count + 1))}
                            className="flex h-5 w-5 items-center justify-center rounded text-xs font-semibold text-dash-on-surface hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <section className="flex flex-col gap-4 rounded-xl border border-dash-outline-variant bg-white p-5">
        <div className="flex flex-col justify-between gap-4 border-b border-dash-outline-variant pb-4 md:flex-row md:items-center">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-medium text-dash-on-surface-variant">Leves (L):</span>
              <span className="text-lg font-bold text-blue-700">{score.mild}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-medium text-dash-on-surface-variant">Graves (G):</span>
              <span className="text-lg font-bold text-amber-700">{score.serious}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-medium text-dash-on-surface-variant">Reprobatorias (R):</span>
              <span className="text-lg font-bold text-red-600">{score.failing}</span>
            </div>
          </div>
          <span className="rounded-md bg-[#f4f6fb] px-3 py-1.5 text-xs text-dash-on-surface-variant">
            Reprueba con: {PRACTICAL_EXAM_FAIL_RULE}
          </span>
        </div>
        <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-12">
          <div className="flex flex-col gap-2 md:col-span-4">
            <span className="text-xs font-semibold tracking-wider text-dash-on-surface uppercase">Estado del examen</span>
            <div className="grid grid-cols-2 gap-2" role="status" aria-live="polite">
              <div className={score.passed ? approved : idle}>
                <span className="material-symbols-outlined text-[18px]" aria-hidden="true">check</span>
                <span>Aprobado</span>
              </div>
              <div className={score.passed ? idle : failed}>
                <span className="material-symbols-outlined text-[18px]" aria-hidden="true">close</span>
                <span>Reprobado</span>
              </div>
            </div>
            <p className={`text-xs font-medium ${score.passed ? 'text-emerald-700' : 'text-red-600'}`}>
              {score.passed
                ? 'Sugerencia: Aprobado (faltas dentro del rango permitido).'
                : 'Sugerencia: Reprobado según umbrales reglamentarios MTT.'}
            </p>
          </div>
          <div className="flex flex-col gap-1.5 md:col-span-8">
            <label className="text-xs font-semibold tracking-wider text-dash-on-surface uppercase" htmlFor="examNotes">
              Observaciones
            </label>
            <textarea
              id="examNotes"
              rows={2}
              readOnly={readOnly}
              value={sheet.observations}
              placeholder="Observaciones o incidencias detectadas en la ruta práctica..."
              onChange={(event) => onObservations(event.target.value)}
              className="w-full resize-none rounded-lg border border-dash-outline-variant bg-[#f4f6fb] px-3 py-2 text-sm text-dash-on-surface transition-all outline-none placeholder:text-dash-on-surface-variant/60 focus:border-[#000256] focus:bg-white"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function kmLabel(value: number | null): string {
  return value == null ? '—' : `${value.toLocaleString('es-CL')} km`;
}

function kmPair(start: number | null, end: number | null): string {
  if (start == null && end == null) return '—';
  return `${kmLabel(start)} — ${kmLabel(end)}`;
}

function SummaryField({ label, value, wide = false, mono = false }: { label: string; value: string; wide?: boolean; mono?: boolean }) {
  return (
    <div className={wide ? 'flex flex-col sm:col-span-2' : 'flex flex-col'}>
      <span className="text-[11px] font-medium text-dash-on-surface-variant">{label}</span>
      <span className={`mt-0.5 font-semibold text-dash-on-surface ${mono ? 'font-mono text-sm' : 'text-sm'}`}>{value || '—'}</span>
    </div>
  );
}

function SignatureCard({ title, name, image, imageAlt }: { title: string; name: string; image: string; imageAlt: string }) {
  return (
    <div className="flex min-h-[190px] flex-col justify-between rounded-xl border border-dash-outline-variant bg-white p-5">
      <div className="border-b border-dash-outline-variant/60 pb-2 text-xs font-semibold text-dash-on-surface">{title}</div>
      <div className="flex flex-col items-center justify-center py-2">
        <div className="flex h-[70px] w-full max-w-[240px] items-center justify-center">
          {image ? <img src={image} alt={imageAlt} className="h-full w-full object-contain" /> : null}
        </div>
        <div className="mt-1 w-3/5 border-b border-dash-outline-variant" />
        <span className="mt-2 text-xs font-semibold text-dash-on-surface">{name || '—'}</span>
      </div>
    </div>
  );
}

export function PracticalExamSummary({
  sheet,
  readOnly = false,
  onObservations,
}: {
  sheet: PracticalExamSheetInput;
  readOnly?: boolean;
  onObservations: (value: string) => void;
}) {
  const score = scorePracticalExam(sheet.faults);
  const marked =
    'flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold';
  const quiet =
    'flex items-center justify-center gap-2 rounded-lg border border-dash-outline-variant bg-[#f4f6fb] px-3 py-2 text-sm font-medium text-dash-on-surface-variant opacity-60';
  const vehicle =
    sheet.vehicleType === 'mechanical' ? 'Mecánico' : sheet.vehicleType === 'automatic' ? 'Automático' : '—';
  const schedule = sheet.startedAt || sheet.endedAt ? `${sheet.startedAt || '—'} — ${sheet.endedAt || '—'}` : '—';

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4 rounded-xl border border-dash-outline-variant bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 border-b border-dash-outline-variant/80 pb-3">
          <span className="material-symbols-outlined text-[18px] text-[#000256]" aria-hidden="true">badge</span>
          <h2 className="text-xs font-bold tracking-wider text-dash-on-surface uppercase">Datos de la ficha y postulante</h2>
        </div>
        <div className="grid grid-cols-1 gap-x-6 gap-y-3.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          <SummaryField label="Nombres y apellidos del postulante" value={sheet.applicantName} />
          <SummaryField label="R.U.N." value={sheet.run} />
          <SummaryField label="Fecha del examen" value={examDateLabel(sheet.examDate)} />
          <SummaryField label="Nombre del examinador" value={sheet.examinerName} />
          <SummaryField label="Municipio" value={sheet.municipality} />
          <SummaryField label="Horario (Inicio / Término)" value={schedule} />
          <SummaryField label="Kilometraje (Inicial / Final)" value={kmPair(sheet.kmStart, sheet.kmEnd)} />
          <SummaryField label="Patente" value={sheet.plate.toUpperCase()} mono />
          <SummaryField label="Escuela de conductores" value={sheet.school} />
          <SummaryField label="Comuna de la escuela" value={sheet.schoolCommune} />
          <SummaryField label="Vehículo" value={vehicle} wide />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {CLASS_B_PRACTICAL_EXAM.map((block, index) => {
          const sub = score.subtotals[block.id] ?? { mild: 0, serious: 0, failing: 0 };
          return (
            <section key={block.id} className="flex flex-col overflow-hidden rounded-lg border border-dash-outline-variant bg-white">
              <header className="flex items-center justify-between gap-2 border-b border-dash-outline-variant bg-[#f4f6fb] px-3.5 py-2.5">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-[#000256] text-xs font-bold text-white">
                    {index + 1}
                  </span>
                  <h2 className="truncate text-sm font-semibold text-dash-on-surface">{block.title}</h2>
                </div>
                <span className="shrink-0 text-xs text-dash-on-surface-variant">
                  L:<b className={sub.mild ? 'font-semibold text-blue-700' : 'font-semibold'}>{sub.mild}</b>{' '}
                  G:<b className={sub.serious ? 'font-semibold text-amber-700' : 'font-semibold'}>{sub.serious}</b>{' '}
                  R:<b className={sub.failing ? 'font-semibold text-red-600' : 'font-semibold'}>{sub.failing}</b>
                </span>
              </header>
              <div className="flex flex-col divide-y divide-dash-outline-variant/50 p-2">
                {block.faults.map((fault) => {
                  const count = sheet.faults[fault.id] ?? 0;
                  const active = count > 0;
                  const tone =
                    fault.severity === 'L'
                      ? 'bg-blue-50/60 text-blue-900'
                      : fault.severity === 'G'
                        ? 'bg-amber-50 text-amber-950'
                        : 'bg-red-50 text-red-900';
                  return (
                    <div
                      key={fault.id}
                      className={`flex items-center justify-between gap-2 rounded px-2 py-1.5 ${active ? tone : 'text-dash-on-surface'}`}
                    >
                      <span className={`pr-2 text-xs leading-tight ${active ? 'font-semibold' : ''}`}>
                        {fault.code} {fault.label}
                        {count > 1 ? ` ×${count}` : ''}
                      </span>
                      <span className={badgeClass(fault.severity, active)}>{fault.severity}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <section className="flex flex-col gap-5 rounded-xl border border-dash-outline-variant bg-white p-5 shadow-sm">
        <div className="flex flex-col justify-between gap-4 border-b border-dash-outline-variant pb-4 md:flex-row md:items-center">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-medium text-dash-on-surface-variant">Leves (L):</span>
              <span className="text-xl font-bold text-blue-700">{score.mild}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-medium text-dash-on-surface-variant">Graves (G):</span>
              <span className="text-xl font-bold text-amber-700">{score.serious}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-medium text-dash-on-surface-variant">Reprobatorias (R):</span>
              <span className="text-xl font-bold text-red-600">{score.failing}</span>
            </div>
          </div>
          <span className="rounded-md border border-dash-outline-variant bg-[#f4f6fb] px-3 py-1.5 text-xs text-dash-on-surface-variant">
            Reprueba con: {PRACTICAL_EXAM_FAIL_RULE}
          </span>
        </div>
        <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-12">
          <div className="flex flex-col gap-2 md:col-span-4">
            <span className="text-xs font-semibold tracking-wider text-dash-on-surface uppercase">Dictamen de la evaluación</span>
            <div className="grid grid-cols-2 gap-2" role="status">
              <div className={score.passed ? `${marked} border-emerald-300 bg-emerald-50 text-emerald-800` : quiet}>
                <span className="material-symbols-outlined text-[18px]" aria-hidden="true">check_circle</span>
                <span>Aprobado</span>
              </div>
              <div className={score.passed ? quiet : `${marked} border-red-300 bg-red-50 text-red-800`}>
                <span className="material-symbols-outlined text-[18px]" aria-hidden="true">cancel</span>
                <span>Reprobado</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1.5 md:col-span-8">
            <label className="text-xs font-semibold tracking-wider text-dash-on-surface uppercase" htmlFor="summaryNotes">
              Observaciones
            </label>
            <textarea
              id="summaryNotes"
              rows={2}
              readOnly={readOnly}
              value={sheet.observations}
              placeholder="Observaciones o incidencias detectadas en la ruta práctica..."
              onChange={(event) => onObservations(event.target.value)}
              className="w-full resize-none rounded-lg border border-dash-outline-variant bg-[#f4f6fb] px-3 py-2 text-sm text-dash-on-surface transition-all outline-none placeholder:text-dash-on-surface-variant/60 focus:border-[#000256] focus:bg-white"
            />
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-xl border border-dash-outline-variant bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2 border-b border-dash-outline-variant/80 pb-3">
          <span className="material-symbols-outlined text-[18px] text-[#000256]" aria-hidden="true">verified_user</span>
          <h2 className="text-xs font-bold tracking-wider text-dash-on-surface uppercase">Firmas examinador y postulante</h2>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <SignatureCard title="Firma del postulante" name={sheet.applicantName} image={sheet.studentSignature} imageAlt="Firma del postulante" />
          <SignatureCard title="Firma del examinador" name={sheet.examinerName} image={sheet.examinerSignature} imageAlt="Firma del examinador" />
        </div>
      </section>
    </div>
  );
}
